import React from 'react';
import useReveal from '../../hooks/useReveal';
import { BAR_ANIMATION_MS, REVEAL_DELAY_MS, REVEAL_DURATION_MS, toMs } from '../../constants/animation';

interface BrandAffinityRendererProps {
  title: string;
  data: any;
  primaryColor?: string;
}

const parseIndex = (value: any) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const getIconForSection = (sectionTitle: string) => {
  const s = sectionTitle.toLowerCase();
  if (s.includes('auto') || s.includes('car')) return '🚗';
  if (s.includes('restaurant') || s.includes('food')) return '🍽️';
  if (s.includes('hotel')) return '🏨';
  if (s.includes('airline') || s.includes('air')) return '✈️';
  return '🛍️';
};

const BrandSectionCard: React.FC<{ sectionTitle: string; brands: any[] }> = ({ sectionTitle, brands }) => {
  const { ref, isVisible } = useReveal<HTMLDivElement>({ threshold: 0.2, once: true });
  if (!brands || brands.length === 0) return null;

  const indices = brands.map((brand: any) => parseIndex(brand.index ?? brand.value));
  const maxIndex = Math.max(120, 100, ...indices);
  const baseline = 100;
  const baselinePct = Math.min(100, (baseline / maxIndex) * 100);
  const barHeight = 'h-8';
  const trackColor = '#E5E7EB';
  const barColor = '#3B6FC4';

  return (
    <div
      ref={ref}
      className="bg-white rounded-xl shadow-md p-6"
      style={{
        transition: `transform ${toMs(REVEAL_DURATION_MS)} ease-out, opacity ${toMs(REVEAL_DURATION_MS)} ease-out`,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        opacity: isVisible ? 1 : 0.25,
        transitionDelay: isVisible ? toMs(REVEAL_DELAY_MS) : '0ms',
      }}
    >
      <div className="flex items-center mb-6">
        <span className="text-2xl mr-2">{getIconForSection(sectionTitle)}</span>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{sectionTitle}</h3>
          <p className="text-xs text-slate-500 uppercase tracking-wide">BY INDEX · MINIMUM COMPOSITION 5%</p>
        </div>
      </div>

      <div className="space-y-5">
        {brands.slice(0, 6).map((brand, idx) => {
          const name = brand.name || brand.label || String(brand);
          const index = parseIndex(brand.index ?? brand.value);
          const widthPct = Math.min(100, (index / maxIndex) * 100);
          const delay = isVisible ? toMs(REVEAL_DELAY_MS + idx * 140) : '0ms';

          return (
            <div key={`${name}-${idx}`} className="flex items-center gap-4">
              <div className="w-32 text-sm font-medium text-slate-800 leading-snug">{name}</div>
              <div className="flex-1">
                <div className={`relative ${barHeight} rounded-full`} style={{ backgroundColor: trackColor }}>
                  <div className="absolute inset-y-0" style={{ left: `${baselinePct}%` }}>
                    <div className="w-[2px] h-full bg-white/85" />
                    <div
                      className="absolute top-full mt-1 text-[10px] font-semibold text-slate-500"
                      style={{ left: '50%', transform: 'translateX(-50%)' }}
                    >
                      100
                    </div>
                  </div>
                  <div
                    className="absolute inset-y-0 left-0 rounded-full flex items-center justify-end pr-3 text-xs font-semibold text-white"
                    style={{
                      width: `${isVisible ? widthPct : 0}%`,
                      minWidth: widthPct > 0 ? '48px' : 0,
                      backgroundColor: barColor,
                      transition: `width ${toMs(BAR_ANIMATION_MS)} ease-out`,
                      transitionDelay: delay,
                    }}
                  >
                    {index}
                  </div>
                </div>
              </div>
              <div className="text-right min-w-[70px]">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Index</div>
                <div className="text-xl font-bold text-slate-900">{index}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-center mt-6 text-[11px] text-slate-400 uppercase tracking-wide">
        Online Adult Population
      </div>
    </div>
  );
};

export default function BrandAffinityRenderer({ title, data, primaryColor = '#3B6FC4' }: BrandAffinityRendererProps) {
  if (!data || typeof data !== 'object') return null;
  void primaryColor;

  const entries = Object.entries(data || {});
  if (entries.length === 0) return null;

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <p className="text-gray-600">BASELINE: Online Adult Population | CONTAINS: Engaged Audiences Data</p>
          </div>
          {(data as any)?.metrics?.projectedAdultPopulation ? (
            <div className="text-right">
              <div className="text-sm text-gray-600">PROJECTED ADULT POPULATION</div>
              <div className="text-3xl font-bold text-gray-900">{(data as any).metrics.projectedAdultPopulation}</div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {(() => {
          const findData = (preds: string[]) => {
            const key = entries.find(([k]) => preds.some((p) => k.toLowerCase().includes(p)))?.[0];
            return key ? (data as any)[key] : [];
          };
          const cards: Array<JSX.Element> = [];
          const spec: Array<{ title: string; preds: string[] }> = [
            { title: 'Top Retailers', preds: ['retailer'] },
            { title: 'Top Apparel Retailers', preds: ['apparel'] },
            { title: 'Top Auto Brands Currently Owned', preds: ['auto', 'car'] },
            { title: 'Top Restaurants Visited', preds: ['restaurant'] },
            { title: 'Top Hotels Tried', preds: ['hotel'] },
            { title: 'Top Airlines Tried', preds: ['airline', 'air '] },
          ];
          spec.forEach((s, idx) => {
            const arr = findData(s.preds);
            if (Array.isArray(arr) && arr.length > 0) {
              cards.push(<BrandSectionCard key={idx} sectionTitle={s.title} brands={arr} />);
            }
          });
          return cards;
        })()}
      </div>
    </div>
  );
}

// touch: refine layout per reference
